import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, School, X, Search, Users, AlertTriangle, UserPlus, ArrowLeftRight, Plus, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DjayAvatar, DjayBubble } from '../../mascot';
import { OnboardingProgress } from './onboarding/OnboardingProgress';
import type { AccountsState, Classroom } from '../types';
import { isTeacherAccount } from '../types';
import { getSchoolClassrooms, getAllClassroomsWithTeachers } from '../logic/classrooms';
import type { ClassroomWithTeacher } from '../logic/classrooms';
interface CreateFirstClassroomProps {
  onCreate: (name: string, grade: number) => void;
  onJoinExisting?: (classroomId: string, mode: 'transfer' | 'coteach') => void;
  onSkip: () => void;
  isLoading?: boolean;
  error?: string;
  accounts?: AccountsState;
  activeTeacherId?: string;
  embedded?: boolean;
}
export function CreateFirstClassroom({
  onCreate,
  onJoinExisting,
  onSkip,
  isLoading,
  error,
  accounts,
  activeTeacherId,
  embedded = false
}: CreateFirstClassroomProps) {
  const [name, setName] = useState('');
  const [grade, setGrade] = useState('6');
  const [mode, setMode] = useState<'browse' | 'create'>('browse');
  const [classSearch, setClassSearch] = useState('');
  const [selectedClassroom, setSelectedClassroom] = useState<ClassroomWithTeacher | null>(null);
  const [joinMode, setJoinMode] = useState<'transfer' | 'coteach' | null>(null);
  // Get current teacher's school name
  const teacherSchoolName = useMemo(() => {
    if (!accounts || !activeTeacherId) return '';
    const teacher = accounts.accountsById[activeTeacherId];
    if (teacher && isTeacherAccount(teacher)) {
      return teacher.schoolName || '';
    }
    return '';
  }, [accounts, activeTeacherId]);
  // Get existing classrooms: prefer same-school, fallback to all
  const {
    classrooms: availableClassrooms,
    isSchoolFiltered
  } = useMemo(() => {
    if (!accounts) return {
      classrooms: [],
      isSchoolFiltered: false
    };
    // Try same-school first
    if (teacherSchoolName) {
      const schoolResults = getSchoolClassrooms(accounts, teacherSchoolName).filter(c => c.teacherId !== activeTeacherId).map(c => ({
        ...c,
        schoolName: teacherSchoolName
      }));
      if (schoolResults.length > 0) {
        return {
          classrooms: schoolResults,
          isSchoolFiltered: true
        };
      }
    }
    // Fallback: all classrooms from other teachers
    const allResults = getAllClassroomsWithTeachers(accounts).filter(c => c.teacherId !== activeTeacherId);
    return {
      classrooms: allResults,
      isSchoolFiltered: false
    };
  }, [accounts, teacherSchoolName, activeTeacherId]);
  // Filter classrooms by search
  const filteredClassrooms = useMemo(() => {
    if (!classSearch.trim()) return availableClassrooms;
    const q = classSearch.toLowerCase();
    return availableClassrooms.filter(c => c.classroom.name.toLowerCase().includes(q) || c.teacherName.toLowerCase().includes(q) || c.schoolName.toLowerCase().includes(q));
  }, [availableClassrooms, classSearch]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate(name.trim(), parseInt(grade, 10));
    }
  };
  const handleJoinExisting = () => {
    if (selectedClassroom && joinMode && onJoinExisting) {
      onJoinExisting(selectedClassroom.classroom.id, joinMode);
    }
  };
  const hasExistingClassrooms = availableClassrooms.length > 0;
  const formCard = <div className="bg-white rounded-3xl shadow-xl border border-indigo-100/80 relative overflow-hidden" data-id="element-34">
      <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 to-blue-400" data-id="element-35" />

      <div className="p-6 sm:p-8" data-id="element-36">
        {embedded && <OnboardingProgress currentStep={1} role="teacher" data-id="element-37" />}

        <AnimatePresence mode="wait" data-id="element-38">
          {/* ── BROWSE MODE ── */}
          {hasExistingClassrooms && mode === 'browse' && !selectedClassroom && <motion.div key="browse" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-39">
              <div className="text-center mb-5" data-id="element-40">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-indigo-100 rounded-2xl mb-3" data-id="element-41">
                  <Search className="w-5 h-5 text-indigo-600" data-id="element-42" />
                </div>
                <h2 className="text-xl font-bold text-gray-900" data-id="element-43">
                  Bestaande klassen
                </h2>
                <p className="text-sm text-gray-500 mt-1" data-id="element-44">
                  {isSchoolFiltered ? `Klassen op ${teacherSchoolName}` : 'Alle beschikbare klassen'}
                </p>
              </div>

              {/* Search input */}
              <div className="relative mb-3" data-id="element-45">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-46" />
                <input type="text" value={classSearch} onChange={e => setClassSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all outline-none text-sm" placeholder="Zoek op klasnaam of leraar..." data-id="element-47" />
              </div>

              {/* Classroom list */}
              <div className="border border-slate-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto mb-4" data-id="element-48">
                {filteredClassrooms.length > 0 ? filteredClassrooms.map(item => <button key={item.classroom.id} type="button" onClick={() => setSelectedClassroom(item)} className="w-full text-left px-3.5 py-3 hover:bg-indigo-50/70 transition-colors border-b border-slate-100 last:border-0 group" data-id="element-49">
                      <div className="flex items-center gap-3" data-id="element-50">
                        <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors" data-id="element-51">
                          <Users className="w-4 h-4 text-indigo-600" data-id="element-52" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-53">
                          <p className="font-semibold text-slate-900 text-sm truncate" data-id="element-54">
                            {item.classroom.name}
                          </p>
                          <p className="text-xs text-slate-500 truncate" data-id="element-55">
                            {item.teacherName}
                            {!isSchoolFiltered && item.schoolName && ` · ${item.schoolName}`}
                            {' · '}Groep {item.classroom.groupLevel}
                            {' · '}
                            {item.classroom.learnerIds.length} leerling
                            {item.classroom.learnerIds.length !== 1 ? 'en' : ''}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors flex-shrink-0" data-id="element-56" />
                      </div>
                    </button>) : <div className="px-4 py-8 text-center" data-id="element-57">
                    <p className="text-slate-500 text-sm" data-id="element-58">
                      Geen klassen gevonden
                      {classSearch ? ` voor "${classSearch}"` : ''}
                    </p>
                  </div>}
              </div>

              {/* Create new button */}
              <div className="space-y-2" data-id="element-59">
                <button type="button" onClick={() => setMode('create')} className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors" data-id="element-60">
                  <Plus className="w-4 h-4" data-id="element-61" />
                  Nieuwe klas aanmaken
                </button>

                {!embedded && <button type="button" onClick={onSkip} disabled={isLoading} className="w-full py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-id="element-62">
                    Sla over voor nu
                  </button>}
              </div>
            </motion.div>}

          {/* ── SELECTED CLASSROOM ── */}
          {selectedClassroom && <motion.div key="selected" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-63">
              {/* Classroom info */}
              <div className="p-4 bg-indigo-50 rounded-2xl mb-4" data-id="element-64">
                <div className="flex items-center gap-3" data-id="element-65">
                  <div className="w-11 h-11 bg-indigo-200 rounded-xl flex items-center justify-center" data-id="element-66">
                    <School className="w-5 h-5 text-indigo-700" data-id="element-67" />
                  </div>
                  <div className="flex-1" data-id="element-68">
                    <p className="font-bold text-slate-900" data-id="element-69">
                      {selectedClassroom.classroom.name}
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-70">
                      Groep {selectedClassroom.classroom.groupLevel} ·{' '}
                      {selectedClassroom.classroom.learnerIds.length} leerling
                      {selectedClassroom.classroom.learnerIds.length !== 1 ? 'en' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 mb-4" data-id="element-71">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" data-id="element-72" />
                <span data-id="element-73">
                  Deze klas is van{' '}
                  <strong data-id="element-74">{selectedClassroom.teacherName}</strong>
                  {selectedClassroom.schoolName && <span className="text-amber-600" data-id="element-75">
                      {' '}
                      ({selectedClassroom.schoolName})
                    </span>}
                </span>
              </div>

              {/* Options */}
              <div className="space-y-2.5 mb-5" data-id="element-76">
                <button type="button" onClick={() => setJoinMode('coteach')} className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${joinMode === 'coteach' ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-200 hover:border-indigo-200'}`} data-id="element-77">
                  <div className="flex items-center gap-3" data-id="element-78">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${joinMode === 'coteach' ? 'bg-indigo-200' : 'bg-slate-100'}`} data-id="element-79">
                      <UserPlus className={`w-4 h-4 ${joinMode === 'coteach' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-80" />
                    </div>
                    <div data-id="element-81">
                      <p className={`font-semibold text-sm ${joinMode === 'coteach' ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-82">
                        Mee-kijken
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-83">
                        Leerlingen zichtbaar, {selectedClassroom.teacherName}{' '}
                        blijft eigenaar
                      </p>
                    </div>
                  </div>
                </button>

                <button type="button" onClick={() => setJoinMode('transfer')} className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${joinMode === 'transfer' ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-slate-200 hover:border-amber-200'}`} data-id="element-84">
                  <div className="flex items-center gap-3" data-id="element-85">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${joinMode === 'transfer' ? 'bg-amber-200' : 'bg-slate-100'}`} data-id="element-86">
                      <ArrowLeftRight className={`w-4 h-4 ${joinMode === 'transfer' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-87" />
                    </div>
                    <div data-id="element-88">
                      <p className={`font-semibold text-sm ${joinMode === 'transfer' ? 'text-amber-900' : 'text-slate-900'}`} data-id="element-89">
                        Klas overnemen
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-90">
                        Jij wordt de nieuwe eigenaar
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Actions */}
              <div className="space-y-2" data-id="element-91">
                <Button type="button" className={`w-full py-5 text-white ${joinMode === 'transfer' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-indigo-600 hover:bg-indigo-700'}`} disabled={!joinMode || isLoading} onClick={handleJoinExisting} data-id="element-92">
                  {isLoading ? 'Bezig...' : joinMode === 'transfer' ? 'Klas overnemen' : joinMode === 'coteach' ? 'Mee-kijken starten' : 'Kies een optie'}
                  {!isLoading && joinMode && <ArrowRight className="w-4 h-4 ml-2" data-id="element-93" />}
                </Button>

                <button type="button" onClick={() => {
              setSelectedClassroom(null);
              setJoinMode(null);
            }} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-94">
                  ← Terug naar overzicht
                </button>
              </div>

              {error && <p className="text-sm text-red-600 mt-3 text-center" data-id="element-95">{error}</p>}
            </motion.div>}

          {/* ── CREATE MODE ── */}
          {(mode === 'create' || !hasExistingClassrooms) && !selectedClassroom && <motion.div key="create" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-96">
                <div className="text-center mb-6" data-id="element-97">
                  <div className="inline-flex items-center justify-center w-11 h-11 bg-indigo-100 rounded-2xl mb-3" data-id="element-98">
                    <School className="w-5 h-5 text-indigo-600" data-id="element-99" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-100">
                    Nieuwe klas aanmaken
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-101">
                    Je kunt later altijd meer klassen toevoegen.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-102">
                  <div className="space-y-4" data-id="element-103">
                    <Input label="Naam van de klas" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Bijv. Groep 7A" required disabled={isLoading} data-id="element-104" />

                    <Select label="Groep" value={grade} onChange={e => setGrade(e.target.value)} options={[{
                value: '6',
                label: 'Groep 6'
              }, {
                value: '7',
                label: 'Groep 7'
              }, {
                value: '8',
                label: 'Groep 8'
              }]} disabled={isLoading} data-id="element-105" />

                    {error && <p className="text-sm text-red-600" data-id="element-106">{error}</p>}
                  </div>

                  <div className="space-y-2 pt-1" data-id="element-107">
                    <Button type="submit" className="w-full py-5 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!name.trim() || isLoading} data-id="element-108">
                      {isLoading ? 'Bezig met aanmaken...' : 'Klas aanmaken'}
                      {!isLoading && <ArrowRight className="w-4 h-4 ml-2" data-id="element-109" />}
                    </Button>

                    {hasExistingClassrooms && <button type="button" onClick={() => {
                setMode('browse');
                setName('');
              }} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-110">
                        ← Terug naar bestaande klassen
                      </button>}

                    {!embedded && <button type="button" onClick={onSkip} disabled={isLoading} className="w-full py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-id="element-111">
                        Sla over voor nu
                      </button>}
                  </div>
                </form>
              </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
  if (embedded) {
    return formCard;
  }
  return <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-50/30 flex flex-col items-center justify-center p-4" data-id="element-112">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="w-full max-w-md" data-id="element-113">
        {/* Mascot Section */}
        <div className="flex flex-col items-center mb-6 relative" data-id="element-114">
          <DjayBubble message={selectedClassroom ? `${selectedClassroom.classroom.name} van ${selectedClassroom.teacherName} — wat wil je doen?` : hasExistingClassrooms && mode === 'browse' ? teacherSchoolName ? `Er zijn al klassen${isSchoolFiltered ? ` op ${teacherSchoolName}` : ''}. Kijk of jouw klas ertussen staat!` : 'Er zijn al klassen beschikbaar. Kijk of jouw klas ertussen staat!' : 'Welkom! Laten we je eerste klas aanmaken.'} variant="welcome" className="mb-3" data-id="element-115" />
          <DjayAvatar emotion={selectedClassroom ? 'thinking' : 'celebrating'} size="lg" data-id="element-116" />
        </div>

        {formCard}
      </motion.div>
    </div>;
}